import { useState, useEffect, useCallback, useMemo } from 'preact/hooks';
import Button from '../components/shared/Button.jsx';
import Card from '../components/shared/Card.jsx';
import DebtorProductsModal from '../components/DebtorProductsModal.jsx';
import PendingChargesModal from '../components/PendingChargesModal.jsx';
import DebtorFormModal from '../components/DebtorFormModal.jsx';
import SignupRequestModal, { ProvisionStatus, differsFromDebtor } from '../components/SignupRequestModal.jsx';
import { formatDate, INTERVAL_LABEL } from '../lib/format.js';

export default function DebtorsView({ onChanged }) {
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [notice, setNotice] = useState(null);
  const [linksTarget, setLinksTarget] = useState(null);
  const [chargesTarget, setChargesTarget] = useState(null);
  const [signups, setSignups] = useState([]);
  const [signupTarget, setSignupTarget] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [sending, setSending] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        fetch('/api/debtors', { credentials: 'same-origin' }),
        fetch('/api/signup-requests', { credentials: 'same-origin' })
      ]);
      if (d.ok) setDebtors((await d.json()).debtors || []);
      if (s.ok) setSignups((await s.json()).requests || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // De aanmelding die bij een aanvraag hoort. Een debiteur die al klant was kan
  // zich ook aanmelden; die staat dan ook bij de openstaande aanvragen.
  const openSignupByDebtor = useMemo(() => {
    const map = new Map();
    for (const r of signups) if (r.status === 'pending') map.set(r.debtorId, r);
    return map;
  }, [signups]);
  const requested = useMemo(
    () => debtors.filter(d => d.status === 'requested' || openSignupByDebtor.has(d.id)),
    [debtors, openSignupByDebtor]
  );
  const active = useMemo(() => debtors.filter(d => d.status === 'active'), [debtors]);
  // Goedgekeurd, maar nog niet aangemaakt in het product.
  const unprovisioned = useMemo(
    () => signups.filter(r => r.status === 'approved' && r.linkMode === 'new' && ['pending', 'failed'].includes(r.provision.status)),
    [signups]
  );

  function afterChange() {
    refresh();
    onChanged?.();
  }

  function openCreate() { setEditTarget(null); setFormOpen(true); }
  function openEdit(debtor) { setEditTarget(debtor); setFormOpen(true); }

  async function approve(debtor, { confirmNewClub = false } = {}) {
    const res = await fetch(`/api/debtors/${debtor.id}/approve`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm_new_club: confirmNewClub })
    });
    if (res.status === 409) {
      // 4U herkende een bestaande klant. Een nieuwe club alleen na uitdrukkelijke
      // bevestiging; anders is koppelen de bedoeling.
      const data = await res.json().catch(() => ({}));
      const names = (data.suggestions || []).map(s => `${s.debtorName} (${s.reason})`).join(', ');
      if (confirm(`Deze aanmelding lijkt van een bestaande klant: ${names}.\n\nGoedkeuren als nieuwe klant maakt er een tweede aan in het product. Weet je het zeker? Kies anders "Koppelen" in de aanmelding.`)) {
        return approve(debtor, { confirmNewClub: true });
      }
      return;
    }
    if (!res.ok) {
      setNotice(`Goedkeuren van "${debtor.name}" is mislukt.`);
      return;
    }
    const data = await res.json();
    const failed = (data.provisioning || []).filter(p => !p.ok);
    setNotice(`"${debtor.name}" is klant. ${data.activatedSubscriptions} abonnement(en) gestart.` +
      (data.provisioning?.length
        ? failed.length
          ? ' Aanmaken in het product is mislukt; zie hieronder.'
          : ' De klant is aangemaakt in het product en de beheerder is uitgenodigd.'
        : '') +
      agreementNotice(data.agreementMails));
    afterChange();
  }

  async function resendMail(request, kind) {
    setSending(`${request.id}:${kind}`);
    try {
      const res = await fetch(`/api/signup-requests/${request.id}/mail`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind })
      });
      const data = await res.json().catch(() => ({}));
      if (data.request && signupTarget?.id === request.id) setSignupTarget(data.request);
      const what = kind === 'agreement' ? '"Overeenkomst rond"' : 'De bevestiging';
      setNotice(data.status === 'sent'
        ? `${what} is opnieuw verstuurd naar ${request.signer.email}.`
        : data.status === 'skipped'
          ? `${what} is niet verstuurd: er is geen SMTP ingesteld.`
          : `${what} versturen is mislukt.`);
      afterChange();
    } finally {
      setSending(null);
    }
  }

  async function retryProvision(request, { confirmNewClub = false, forceNewClub = false } = {}) {
    setRetrying(request.id);
    try {
      const res = await fetch(`/api/signup-requests/${request.id}/provision`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_new_club: confirmNewClub, force_new_club: forceNewClub })
      });
      const data = await res.json().catch(() => ({}));
      if (data.request && signupTarget?.id === request.id) setSignupTarget(data.request);
      setNotice(data.result?.joined
        ? `Er liep al een poging voor "${request.debtorName}"; dit is de uitkomst daarvan. Probeer het zo nodig opnieuw.`
        : data.result?.ok
          ? `"${request.debtorName}" is aangemaakt in het product als ${data.result.accountRef}.`
          : `Aanmaken van "${request.debtorName}" is weer mislukt.`);
      afterChange();
    } finally {
      setRetrying(null);
    }
  }

  // Afwijzen via de aanmelding: de server weet of er een debiteur gearchiveerd,
  // een aangevraagd abonnement opgezegd of alleen de aanmelding afgewezen moet
  // worden. Why: bij een bestaande klant hangt de aanmelding aan zijn lopende
  // abonnement, en dat mag afwijzen juist niet raken.
  async function reject(debtor, signup) {
    if (!signup) return archive(debtor);
    const existing = debtor.status === 'active';
    if (!confirm(existing
      ? `De aanmelding van "${debtor.name}" afwijzen? De klant en zijn abonnement blijven bestaan.`
      : `De aanmelding van "${debtor.name}" afwijzen? De aanvraag verdwijnt uit het overzicht.`)) return;
    const res = await fetch(`/api/signup-requests/${signup.id}/reject`, { method: 'POST', credentials: 'same-origin' });
    if (res.ok) afterChange();
    else setNotice(`Afwijzen van de aanmelding van "${debtor.name}" is mislukt.`);
  }

  function linked(request, agreementMail) {
    setSignupTarget(request);
    setNotice(`De aanmelding is gekoppeld aan "${request.debtorName}". Er is niets aangemaakt in het product.` +
      agreementNotice([{ status: agreementMail }]));
    afterChange();
  }

  async function archive(debtor) {
    if (!confirm(`"${debtor.name}" archiveren? De debiteur verdwijnt uit het overzicht.`)) return;
    const res = await fetch(`/api/debtors/${debtor.id}`, { method: 'DELETE', credentials: 'same-origin' });
    if (res.ok) afterChange();
  }

  return (
    <div class="debtors-tab">
      <div class="dashboard-section-head">
        <h2>Debiteuren</h2>
        <Button onClick={openCreate}>+ Debiteur toevoegen</Button>
      </div>

      {notice && (
        <div class="inline-notice" role="status">
          {notice}
          <button type="button" class="inline-notice-close" onClick={() => setNotice(null)} aria-label="Melding sluiten">&times;</button>
        </div>
      )}

      {requested.length > 0 && (
        <Card title={`Openstaande aanvragen (${requested.length})`}>
          <table class="data-table">
            <thead>
              <tr><th>Organisatie</th><th>Pakket</th><th>Ingang</th><th>Aangevraagd</th><th /></tr>
            </thead>
            <tbody>
              {requested.map(d => {
                const signup = openSignupByDebtor.get(d.id);
                return (
                <tr key={d.id}>
                  <td>
                    {d.name}<div class="muted">{d.email}</div>
                    {d.status === 'active' && <div class="muted">bestaande klant</div>}
                    {signup?.suggestion && (
                      <div class="form-warning-inline">Lijkt op bestaande klant {signup.suggestion.debtorName} ({signup.suggestion.reason})</div>
                    )}
                    {signup && !signup.suggestion && differsFromDebtor(signup) && (
                      <div class="form-warning-inline">Ingevuld: {signup.order.customer.name}, KvK {signup.order.customer.kvkNr}</div>
                    )}
                  </td>
                  <td>{signup ? `${signup.order.plan.name}, ${INTERVAL_LABEL[signup.order.plan.billingInterval]}` : <span class="muted">—</span>}</td>
                  <td class="muted">{signup ? formatDate(signup.startDate) : '—'}</td>
                  <td class="muted">{formatDate(signup?.createdAt ?? d.createdAt)}</td>
                  <td class="row-actions">
                    {signup && <Button size="sm" variant="ghost" onClick={() => setSignupTarget(signup)}>Aanmelding</Button>}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}>{signup ? 'Gegevens' : 'Bekijken'}</Button>
                    {signup?.linkMode === 'existing' || signup?.suggestion
                      ? <Button size="sm" onClick={() => setSignupTarget(signup)}>Koppelen…</Button>
                      : null}
                    {signup?.linkMode !== 'existing' && (
                      <Button size="sm" variant={signup?.suggestion ? 'ghost' : undefined} onClick={() => approve(d)}>
                        {signup?.suggestion ? (signup.order.product.slug === 'pand4u' ? 'Nieuwe organisatie' : 'Nieuwe club') : 'Goedkeuren'}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => reject(d, signup)}>Afwijzen</Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {unprovisioned.length > 0 && (
        <Card title={`Nog niet aangemaakt in het product (${unprovisioned.length})`}>
          {unprovisioned.map(r => (
            <div key={r.id} class="unprovisioned-row">
              <div class="unprovisioned-head">
                <strong>{r.debtorName}</strong>
                <Button size="sm" variant="ghost" onClick={() => setSignupTarget(r)}>Aanmelding</Button>
              </div>
              <ProvisionStatus request={r} onRetry={retryProvision} retrying={retrying === r.id} />
            </div>
          ))}
        </Card>
      )}

      {loading ? (
        <Card><div class="empty-state muted">Debiteuren laden…</div></Card>
      ) : active.length === 0 ? (
        <Card>
          <div class="empty-state">
            <p>Nog geen debiteuren.</p>
            <p class="muted">Voeg er zelf één toe, of keur een aanvraag goed die via de website binnenkomt.</p>
          </div>
        </Card>
      ) : (
        <Card title={`Klanten (${active.length})`}>
          <table class="data-table">
            <thead>
              <tr><th>Organisatie</th><th>E-mail</th><th>KvK</th><th>Plaats</th><th>Klant sinds</th><th /></tr>
            </thead>
            <tbody>
              {active.map(d => (
                <tr key={d.id}>
                  <td>{d.name}</td>
                  <td>{d.email}</td>
                  <td class="muted">{d.kvkNr}</td>
                  <td class="muted">{d.address.city}</td>
                  <td class="muted">{formatDate(d.createdAt)}</td>
                  <td class="row-actions">
                    <Button size="sm" variant="ghost" onClick={() => setLinksTarget(d)}>Koppelingen</Button>
                    <Button size="sm" variant="ghost" onClick={() => setChargesTarget(d)}>Losse posten</Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}>Bewerken</Button>
                    <Button size="sm" variant="ghost" onClick={() => archive(d)}>Archiveren</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <SignupRequestModal
        request={signupTarget}
        onClose={() => setSignupTarget(null)}
        onRetry={retryProvision}
        retrying={retrying === signupTarget?.id}
        onResend={resendMail}
        sending={sending}
        onLinked={linked}
      />

      <DebtorFormModal
        isOpen={formOpen}
        debtor={editTarget}
        onClose={() => setFormOpen(false)}
        onSaved={afterChange}
      />

      <DebtorProductsModal
        isOpen={linksTarget != null}
        debtor={linksTarget}
        onClose={() => setLinksTarget(null)}
        onChanged={afterChange}
      />

      <PendingChargesModal
        isOpen={chargesTarget != null}
        debtor={chargesTarget}
        onClose={() => setChargesTarget(null)}
        onChanged={afterChange}
      />
    </div>
  );
}

function agreementNotice(mails) {
  if (!mails?.length) return '';
  if (mails.every(m => m.status === 'sent')) return ' De mail "overeenkomst rond" is verstuurd.';
  if (mails.some(m => m.status === 'skipped')) return ' De mail "overeenkomst rond" is niet verstuurd: geen SMTP ingesteld.';
  return ' De mail "overeenkomst rond" is mislukt; verstuur hem opnieuw vanuit de aanmelding.';
}
